// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {getElectronAPI, isDesktop} from '@app/features/ui/utils/NativeUtils';
import type {SearchableSettingDescriptor} from '@app/features/user/components/settings_utils/search_index/SearchIndexTypes';
import {msg} from '@lingui/core/macro';

const SHOW_NEW_DEVICE_ALERTS_DESCRIPTOR = msg({
	message: 'Show new device alerts',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const DEVICE_ALERTS_DESCRIPTOR = msg({
	message: 'Device alerts',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const AUDIO_DEVICE_ALERTS_DESCRIPTOR = msg({
	message: 'Audio device alerts',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const NEW_MICROPHONE_DESCRIPTOR = msg({
	message: 'New microphone',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const PROMPT_WHEN_A_NEW_AUDIO_DEVICE_CONNECTS_DESCRIPTOR = msg({
	message: 'Prompt when a new audio device connects',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const AUTOMATIC_GAIN_CONTROL_DESCRIPTOR = msg({
	message: 'Automatic gain control',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const GAIN_CONTROL_DESCRIPTOR = msg({
	message: 'Gain control',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const MICROPHONE_GAIN_DESCRIPTOR = msg({
	message: 'Microphone gain',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CUSTOM_VOICE_PROCESSING_DESCRIPTOR = msg({
	message: 'Custom voice processing',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CONTROL_BROWSER_MICROPHONE_GAIN_FOR_CUSTOM_VOICE_PROCESSING_DESCRIPTOR = msg({
	message: 'Control browser microphone gain for custom voice processing',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const SCREEN_SHARE_CODEC_DESCRIPTOR = msg({
	message: 'Screen share codec',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const SCREEN_SHARING_CODEC_DESCRIPTOR = msg({
	message: 'Screen sharing codec',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CODEC_DESCRIPTOR = msg({
	message: 'Codec',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const VIDEO_CODEC_DESCRIPTOR = msg({
	message: 'Video codec',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CHOOSE_THE_VIDEO_CODEC_FOR_SCREEN_SHARING_DESCRIPTOR = msg({
	message: 'Choose the video codec for screen sharing',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const OPENH264_VIDEO_CODEC_DESCRIPTOR = msg({
	message: 'OpenH264 video codec',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const H264_DESCRIPTOR = msg({
	message: 'H.264',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CISCO_DESCRIPTOR = msg({
	message: 'Cisco',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const ENABLE_OPENH264_SOFTWARE_ENCODING_AND_DECODING_ON_LINUX_DESCRIPTOR = msg({
	message: 'Enable OpenH264 software encoding and decoding on Linux',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const SCREEN_SHARE_PREVIEW_BEHAVIOR_DESCRIPTOR = msg({
	message: 'Screen share preview behavior',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const SCREEN_SHARE_PREVIEW_DESCRIPTOR = msg({
	message: 'Screen share preview',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const PICTURE_IN_PICTURE_DESCRIPTOR = msg({
	message: 'Picture in picture',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const PIP_DESCRIPTOR = msg({
	message: 'PiP',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const STREAM_PREVIEW_DESCRIPTOR = msg({
	message: 'Stream preview',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const CONTROL_BACKGROUND_PREVIEW_POPOUT_AND_STREAM_THUMBNAIL_BEHAVIOR_DESCRIPTOR = msg({
	message: 'Control background preview, popout, and stream thumbnail behavior',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});
const SCREEN_SHARE_ENCODER_CONTROLS_DESCRIPTOR = msg({
	message: 'Screen share encoder controls',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});
const ENCODER_PATH_DESCRIPTOR = msg({
	message: 'Encoder path',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const SOFTWARE_ENCODER_DESCRIPTOR = msg({
	message: 'Software encoder',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const HARDWARE_ENCODER_DESCRIPTOR = msg({
	message: 'Hardware encoder',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const SVC_DESCRIPTOR = msg({
	message: 'SVC',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const BITRATE_DESCRIPTOR = msg({
	message: 'Bitrate',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});
const TUNE_LOW_LEVEL_SCREEN_SHARE_ENCODER_BEHAVIOR_FOR_THE_NEXT_STREAM_DESCRIPTOR = msg({
	message: 'Tune low-level screen-share encoder behavior for the next stream',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});

export const voiceVideoIndex: Array<SearchableSettingDescriptor> = [
	{
		id: 'voice-video-new-device-alerts',
		tabType: 'voice_video',
		sectionId: 'audio',
		label: SHOW_NEW_DEVICE_ALERTS_DESCRIPTOR,
		keywords: [DEVICE_ALERTS_DESCRIPTOR, AUDIO_DEVICE_ALERTS_DESCRIPTOR, NEW_MICROPHONE_DESCRIPTOR],
		description: PROMPT_WHEN_A_NEW_AUDIO_DEVICE_CONNECTS_DESCRIPTOR,
		audience: 'advanced',
		tags: ['voice'],
	},
	{
		id: 'voice-video-automatic-gain-control',
		tabType: 'voice_video',
		sectionId: 'audio',
		label: AUTOMATIC_GAIN_CONTROL_DESCRIPTOR,
		keywords: [GAIN_CONTROL_DESCRIPTOR, MICROPHONE_GAIN_DESCRIPTOR, CUSTOM_VOICE_PROCESSING_DESCRIPTOR],
		description: CONTROL_BROWSER_MICROPHONE_GAIN_FOR_CUSTOM_VOICE_PROCESSING_DESCRIPTOR,
		audience: 'primary',
		tags: ['voice'],
	},
	{
		id: 'voice-video-screen-share-codec',
		tabType: 'voice_video',
		sectionId: 'video',
		label: SCREEN_SHARE_CODEC_DESCRIPTOR,
		keywords: [SCREEN_SHARING_CODEC_DESCRIPTOR, CODEC_DESCRIPTOR, VIDEO_CODEC_DESCRIPTOR],
		description: CHOOSE_THE_VIDEO_CODEC_FOR_SCREEN_SHARING_DESCRIPTOR,
		audience: 'advanced',
		tags: ['media', 'voice'],
	},
	{
		id: 'voice-video-openh264-codec',
		tabType: 'voice_video',
		sectionId: 'video',
		label: OPENH264_VIDEO_CODEC_DESCRIPTOR,
		keywords: [H264_DESCRIPTOR, CODEC_DESCRIPTOR, CISCO_DESCRIPTOR],
		description: ENABLE_OPENH264_SOFTWARE_ENCODING_AND_DECODING_ON_LINUX_DESCRIPTOR,
		audience: 'advanced',
		tags: ['media', 'voice'],
		isVisible: () => isDesktop() && getElectronAPI()?.platform === 'linux',
	},
	{
		id: 'voice-video-screen-share-preview-behavior',
		tabType: 'voice_video',
		sectionId: 'video',
		label: SCREEN_SHARE_PREVIEW_BEHAVIOR_DESCRIPTOR,
		keywords: [
			SCREEN_SHARE_PREVIEW_DESCRIPTOR,
			PICTURE_IN_PICTURE_DESCRIPTOR,
			PIP_DESCRIPTOR,
			STREAM_PREVIEW_DESCRIPTOR,
		],
		description: CONTROL_BACKGROUND_PREVIEW_POPOUT_AND_STREAM_THUMBNAIL_BEHAVIOR_DESCRIPTOR,
		audience: 'advanced',
		tags: ['media', 'privacy'],
	},
	{
		id: 'voice-video-screen-share-encoder-controls',
		tabType: 'voice_video',
		sectionId: 'video',
		label: SCREEN_SHARE_ENCODER_CONTROLS_DESCRIPTOR,
		keywords: [
			ENCODER_PATH_DESCRIPTOR,
			SOFTWARE_ENCODER_DESCRIPTOR,
			HARDWARE_ENCODER_DESCRIPTOR,
			SVC_DESCRIPTOR,
			BITRATE_DESCRIPTOR,
			PRODUCT_NAME,
		],
		description: TUNE_LOW_LEVEL_SCREEN_SHARE_ENCODER_BEHAVIOR_FOR_THE_NEXT_STREAM_DESCRIPTOR,
		audience: 'advanced',
		tags: ['media', 'voice'],
	},
];
